type KinesisTrackingData = Record<string, unknown>;

/**
 * adapter 가 host tracker 에 요구하는 최소 계약 (ISP — adapter 가 실제 소비하는 표면만 명시).
 * `@yanolja-rnd/ya-tracker` · `@yanolja-org/ya-tracker` 의 `KinesisTracker` export 는 이 계약을 구조적으로 만족
 * (extra method 는 무해) — host 는 어느 쪽 패키지의 것이든 그대로 주입 가능.
 *
 * 로컬 계약으로 정의해 adapter 의 배포 `.d.ts` 가 외부 tracker 패키지 타입에 의존하지 않도록 함
 * (host tsc 가 특정 ya-tracker 없이도 adapter 소비 가능).
 */
export interface KinesisTrackerContract {
  view: (pageName: string, data?: KinesisTrackingData) => Promise<void>;
  click: (pageName: string, data?: KinesisTrackingData) => Promise<void>;
}

export interface CoreUiAdapterConfig {
  /**
   * 호스트가 초기화한 KinesisTracker 인스턴스.
   * `@yanolja-rnd/ya-tracker` 또는 `@yanolja-org/ya-tracker` 의 `KinesisTracker` export 를 그대로 넘기면 됨.
   *
   * 다른 tracker export (GA4Tracker / BrazeTracker / CriteoTracker) 는 계약 shape 이 달라 구조적으로 거부됨.
   * 런타임 의존성은 없음 — adapter 는 host 가 주입한 인스턴스를 그대로 참조 (DI 의도 보존).
   */
  kinesisTracker: KinesisTrackerContract;
  /**
   * init 시점 로깅 콜백 (디버깅용).
   * `replaced: true` 면 HMR 또는 중복 init — 의도치 않으면 호스트 부트스트랩 점검.
   */
  onInit?: (info: { isClient: boolean; replaced: boolean }) => void;
}

// 모듈 단위 single source. adapter 가 자체 default import 안 함 — host 주입이 유일 진입점.
let kinesisTracker: KinesisTrackerContract | null = null;
let hasWarnedNotInit = false;

const NOOP_TRACKER: KinesisTrackerContract = {
  view: () => Promise.resolve(),
  click: () => Promise.resolve(),
};

/**
 * core-ui-adapter 부트스트랩 진입점.
 * 호스트의 `_app.tsx` / `app/providers.tsx` 의 client-side useEffect / useLayoutEffect 안에서 호출.
 * SSR 환경에선 server-side mutable global 이 요청 간 leak 될 risk 가 있어 *client-only* 호출 권장.
 */
export function initializeCoreUiAdapter({ kinesisTracker: _kinesisTracker, onInit }: CoreUiAdapterConfig): void {
  const replaced = kinesisTracker !== null;
  kinesisTracker = _kinesisTracker;
  onInit?.({ isClient: typeof window !== 'undefined', replaced });
}

/**
 * SDK 내부에서 tracker 접근.
 * init 누락 시 NOOP_TRACKER 반환 — 호출 코드는 항상 안전 (NPE 방지),
 * dev 환경에서만 console.warn 1회 — 누락을 silent 하게 두지 않는다.
 */
export function getKinesisTracker(): KinesisTrackerContract {
  if (kinesisTracker !== null) return kinesisTracker;

  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && !hasWarnedNotInit) {
    hasWarnedNotInit = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[core-ui-adapter] initializeCoreUiAdapter() 미호출 — host 부트스트랩에서 호출 누락. ' +
        'tracking 호출이 NOOP 으로 처리되어 로그가 손실됩니다. ' +
        '호스트의 _app.tsx 또는 app/providers.tsx 에서 initializeCoreUiAdapter({ kinesisTracker }) 호출을 추가하세요.',
    );
  }

  return NOOP_TRACKER;
}
